import { useCallback, useEffect, useRef, useState } from 'react';
import { isUnknownPayment } from '../utils/paymentResult';

// One bounded polling loop per mounted screen. Only reads are passed as refresh.
export default function usePaymentResultRefresh(rows, refresh, onConfirmed, foreground = false) {
  const latest = useRef({ refresh, onConfirmed });
  latest.current = { refresh, onConfirmed };
  const observed = useRef(new Map());
  const started = useRef(null);
  const busy = useRef(false);
  const [checking, setChecking] = useState(false);
  const [expired, setExpired] = useState(false);
  const hasUnknown = rows.some(isUnknownPayment);
  useEffect(() => {
    for (const row of rows) {
      const id = row.paymentTransactionId ?? row.payment_transaction_id ?? row.id;
      if (id == null) continue;
      const key = String(id);
      if (isUnknownPayment(row)) observed.current.set(key, row);
      else if (observed.current.has(key)) {
        const status = String(row.transaction_status ?? row.transactionStatus ?? row.status ?? '').toUpperCase();
        if (['COMPLETED', 'CANCELED', 'PAID'].includes(status)) {
          observed.current.delete(key);
          latest.current.onConfirmed?.(row);
        }
      }
    }
  }, [rows]);
  const check = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setChecking(true);
    try { await latest.current.refresh(); } catch (_) { /* Unknown remains unknown on lookup errors. */ }
    finally { busy.current = false; setChecking(false); }
  }, []);
  useEffect(() => {
    if (!hasUnknown) return undefined;
    if (started.current == null) started.current = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started.current >= 180000) { setExpired(true); clearInterval(timer); return; }
      if (!document.hidden && (foreground || !window.__paymentResultDialogOpen)) check();
    }, 10000);
    return () => clearInterval(timer);
  }, [hasUnknown, check, foreground]);
  return { hasUnknown, checking, expired, check };
}
